/**
 * Complete climate indicators configuration
 * Technical metadata for each indicator: icons, colors, formatting, preselection, enable condition
 *
 * valueForScale: numeric transformation for Y-axis calculations (must return NUMBER)
 * valueForDisplay: transformation for text display (can return STRING)
 *
 * @file Indicator configuration definitions
 */

import { indicatorsColorScales } from './colorScales.js';
import { indicatorsFormatting, tooltipTitle } from './indicatorsFormatting.js';
import OVERSEAS_TERRITORIES from './overseasTerritories.js';

/**
 * @type {Object}
 */
const indicatorsConfiguration = {
  // Climate Indicators (G1-G5)
  G1: {
    iconName: 'Picto_temperature_moyenne.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.oneDecimal(value),
    valueForDisplay: value => indicatorsFormatting.oneDecimal(value).toFixed(1),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  G2: {
    iconName: 'Picto_jour_gel.svg',
    color: indicatorsColorScales?.darkBlue,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  G3: {
    iconName: 'Picto_cumul_pluie.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  G4: {
    iconName: 'Picto_jour_pluie.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  G5: {
    iconName: 'Picto_jour_pluie.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => OVERSEAS_TERRITORIES.domains.includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },

  // Natural Risks Indicators (R1-R9)
  R1: {
    iconName: 'Picto_jour_pluie__intense.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  R2: {
    iconName: 'Picto_pluie_exceptionnellle.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  R3: {
    iconName: 'Picto_niveau_mer.svg',
    color: indicatorsColorScales?.blue,
    valueForScale: value => indicatorsFormatting.multiply100(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    enable: entity => Boolean(entity?.commune_littorale) || OVERSEAS_TERRITORIES.isOverseas(entity?.domaine),
    tooltipTitle: tooltipTitle.withoutSeasonOrAnnual,
  },
  R4: {
    iconName: 'Picto_feu_foret.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  R5: {
    iconName: 'Picto_secheresse_sol.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  R6: {
    iconName: 'Picto_jour_pluie.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => OVERSEAS_TERRITORIES.domains.includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },
  R7: {
    iconName: 'Picto_jour_pluie__intense.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => OVERSEAS_TERRITORIES.REUNION_MAYOTTE.includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },
  R8: {
    iconName: 'Picto_cyclone.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => [
      OVERSEAS_TERRITORIES.REUNION,
      OVERSEAS_TERRITORIES.MAYOTTE,
      OVERSEAS_TERRITORIES.NOUVELLE_CALEDONIE,
      ...OVERSEAS_TERRITORIES.POLYNESIE_ALL,
      OVERSEAS_TERRITORIES.GUADELOUPE,
      OVERSEAS_TERRITORIES.MARTINIQUE,
      OVERSEAS_TERRITORIES.SAINT_MARTIN_SAINT_BARTHELEMY,
    ].includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
    hideChart: true, // Cyclone indicator displays text only, no chart
  },
  R9: {
    iconName: 'Picto_jour_pluie__intense.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => [
      OVERSEAS_TERRITORIES.GUYANE,
      OVERSEAS_TERRITORIES.NOUVELLE_CALEDONIE,
      ...OVERSEAS_TERRITORIES.POLYNESIE_ALL,
      OVERSEAS_TERRITORIES.GUADELOUPE,
      OVERSEAS_TERRITORIES.MARTINIQUE,
      OVERSEAS_TERRITORIES.SAINT_MARTIN_SAINT_BARTHELEMY,
    ].includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },

  // Health Indicators (S1-S10)
  S1: {
    iconName: 'Picto_jour_tres_chaud.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => !entity?.domaine || entity?.domaine === OVERSEAS_TERRITORIES.REUNION || entity?.domaine === OVERSEAS_TERRITORIES.MAYOTTE || entity?.domaine === OVERSEAS_TERRITORIES.GUYANE,
    tooltipTitle: tooltipTitle.standard,
  },
  S2: {
    iconName: 'Picto_nuit_chaude.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  S3: {
    iconName: 'Picto_vague_chaleur.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  S4: {
    iconName: 'Picto_vague_froid.svg',
    color: indicatorsColorScales?.darkBlue,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => true,
    tooltipTitle: tooltipTitle.standard,
  },
  S5: {
    iconName: 'Picto_nuit_chaude.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => [
      ...OVERSEAS_TERRITORIES.REUNION_MAYOTTE,
      ...OVERSEAS_TERRITORIES.POLYNESIE_ALL,
    ].includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },
  S6: {
    iconName: 'Picto_jour_tres_chaud.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.oneDecimal(value),
    valueForDisplay: value => indicatorsFormatting.oneDecimal(value).toFixed(1),
    preselect: entity => OVERSEAS_TERRITORIES.domains.includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },
  S7: {
    iconName: 'Picto_nuit_chaude.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.oneDecimal(value),
    valueForDisplay: value => indicatorsFormatting.oneDecimal(value).toFixed(1),
    preselect: entity => OVERSEAS_TERRITORIES.domains.includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },
  S8: {
    iconName: 'Picto_nuit_chaude.svg',
    color: indicatorsColorScales?.darkBlue,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => entity?.domaine === OVERSEAS_TERRITORIES.GUYANE,
    tooltipTitle: tooltipTitle.standard,
  },
  S9: {
    iconName: 'Picto_jour_tres_chaud.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => [
      OVERSEAS_TERRITORIES.NOUVELLE_CALEDONIE,
      ...OVERSEAS_TERRITORIES.POLYNESIE_ALL,
      OVERSEAS_TERRITORIES.GUADELOUPE,
    ].includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },
  S10: {
    iconName: 'Picto_nuit_chaude.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => [
      OVERSEAS_TERRITORIES.NOUVELLE_CALEDONIE,
      OVERSEAS_TERRITORIES.GUADELOUPE,
      OVERSEAS_TERRITORIES.MARTINIQUE,
      OVERSEAS_TERRITORIES.SAINT_MARTIN_SAINT_BARTHELEMY,
    ].includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },
  S11: {
    iconName: 'Picto_jour_tres_chaud.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => [
      OVERSEAS_TERRITORIES.GUADELOUPE,
      OVERSEAS_TERRITORIES.MARTINIQUE,
      OVERSEAS_TERRITORIES.SAINT_MARTIN_SAINT_BARTHELEMY,
    ].includes(entity?.domaine),
    tooltipTitle: tooltipTitle.standard,
  },

  // Agriculture Indicators (AG1-AG10)
  AG1: {
    iconName: 'Picto_jour_sans_pluie.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG2: {
    iconName: 'Picto_reprise_vegetation.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => value, // Keep raw numeric value for Y-axis calculations
    valueForDisplay: value => indicatorsFormatting.jourJulien(value), // Convert to date string for display
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG3: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG4: {
    iconName: 'Picto_jour_echaudant.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG5: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG6: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG7: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG8: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG9: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG10: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG11: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG12: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  AG13: {
    iconName: 'Picto_disponibilite_thermique_ble.svg',
    color: indicatorsColorScales?.green,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },

  // Tourism Indicators (T1-T7)
  T1: {
    iconName: 'Picto_jour_estival.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  T2: {
    iconName: 'Picto_enneigement_basse_altitude.svg',
    color: indicatorsColorScales?.darkBlue,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => Boolean(entity?.massif_rattachement),
    enable: entity => Boolean(entity?.massif_rattachement),
    tooltipTitle: tooltipTitle.standard,
  },
  T3: {
    iconName: 'Picto_enneigement_haute_altitude.svg',
    color: indicatorsColorScales?.darkBlue,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: entity => Boolean(entity?.massif_rattachement),
    enable: entity => Boolean(entity?.massif_rattachement),
    tooltipTitle: tooltipTitle.standard,
  },
  T4: {
    iconName: 'Picto_jour_estival.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  T5: {
    iconName: 'Picto_jour_pluie.svg',
    color: indicatorsColorScales?.violet,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  T6: {
    iconName: 'Picto_jour_estival.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
  T7: {
    iconName: 'Picto_jour_estival.svg',
    color: indicatorsColorScales?.red,
    valueForScale: value => indicatorsFormatting.round(value),
    valueForDisplay: value => indicatorsFormatting.round(value),
    preselect: _entity => false,
    tooltipTitle: tooltipTitle.standard,
  },
};

export default indicatorsConfiguration;
